import EmberService, { service } from '@ember/service';

import type { APIData, Service } from 'webapp-types';
import { BillingCancellationOptionType, BillingCancellationReason } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import AccountBillingCancellationFlowPopupConfirmationComponent from 'webapp/components/account/billing/cancellation-flow/popup-confirmation';
import AccountBillingCancellationFlowPopupDowngradeLimitationsComponent from 'webapp/components/account/billing/cancellation-flow/popup-downgrade-limitations';
import AccountBillingCancellationFlowPopupFeedbackComponent from 'webapp/components/account/billing/cancellation-flow/popup-feedback';
import AccountBillingCancellationFlowPopupReactivateComponent from 'webapp/components/account/billing/cancellation-flow/popup-reactivate';
import AccountBillingCancellationFlowPopupRetentionOfferComponent from 'webapp/components/account/billing/cancellation-flow/popup-retention-offer';
import AccountBillingCancellationFlowPopupSomethingWrongComponent from 'webapp/components/account/billing/cancellation-flow/popup-something-wrong';
import AccountBillingCancellationFlowPopupSubscriptionChangedComponent from 'webapp/components/account/billing/cancellation-flow/popup-subscription-changed';

import type BillingPlan from 'webapp/models-custom/billing-plan';
import type PopupModal from 'webapp/models-custom/popup/modal';

import { Defaults } from 'webapp/services/account';
import type { TrackingOriginForSubscriptionChangePopup } from 'webapp/services/billing';

// Cancellation Flow
// https://app.asana.com/0/527984279702626/1202987451883035/f

type TrackingOriginForCancellationFlowStarted = 'downgrade' | 'cancel_subscription' | 'delete';

interface CancellationFeedback {
	choice?: string; // saved in popup-feedback
	trial_downgrade_feedback?: string; // saved in popup-something-wrong
}

export default class FlowCancellationService extends EmberService {
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;

	popupPath = 'account/billing/cancellation-flow';
	popupClass = 'popup-type-account-billing-cancellation-flow';

	latestActivePlan: BillingPlan | null = null;

	get billingPopupConfirmNotificationText(): string {
		let notificationText = '';

		if (this.billingService.isPaused) {
			notificationText =
				'Any changes to your plan will end the paused period and re-activate the subscription.';
		} else if (this.billingService.subscription?.isExclusiveDiscount) {
			notificationText = 'Any changes to your current plan will end the discounted period.';
		}

		return notificationText;
	}

	get billingNotificationText(): string {
		let notificationText = '';
		const billingPopupConfirmNotificationText = this.billingPopupConfirmNotificationText;

		if (billingPopupConfirmNotificationText) {
			if (this.billingService.isPaused) {
				notificationText = 'Your subscription is paused at the moment.';
			} else if (this.billingService.subscription?.isExclusiveDiscount) {
				notificationText = 'Your subscription is discounted at the moment.';
			}

			notificationText += ' ' + billingPopupConfirmNotificationText;
		}

		return notificationText;
	}

	get canRequestSetupHelp(): boolean {
		return !this.currentUserService.isClientUserInPaidClientSpace;
	}

	get disabledTooltipTextForAddonsAndServices(): string {
		return this.billingService.isPaused
			? 'Subscription is paused, re-activate it to access this functionality.'
			: '';
	}

	// LOGIC TO PREPARE/OPEN DIFFERENT POPUPS
	openSomethingWrongPopup(): void {
		this.popupService
			.createModal(AccountBillingCancellationFlowPopupSomethingWrongComponent, {
				popupClass: `${this.popupClass} popup-something-wrong`,
			})
			.open();
	}

	openFeedbackPopup(): void {
		this.popupService
			.createModal(AccountBillingCancellationFlowPopupFeedbackComponent, {
				popupClass: `${this.popupClass} popup-feedback`,
			})
			.open();
	}

	openConfirmationPopup(): void {
		this.popupService
			.createModal(AccountBillingCancellationFlowPopupConfirmationComponent, {
				popupClass: `${this.popupClass} popup-confirmation`,
			})
			.open();
	}

	prepareDowngradeLimitationsPopup(): PopupModal {
		return this.popupService.createModal(
			AccountBillingCancellationFlowPopupDowngradeLimitationsComponent,
			{
				popupClass: `${this.popupClass} popup-downgrade-limitations`,
			},
		);
	}

	openSubscriptionChangedPopup(): void {
		this.popupService
			.createModal(AccountBillingCancellationFlowPopupSubscriptionChangedComponent, {
				popupClass: `${this.popupClass} popup-subscription-changed`,
			})
			.open();
	}

	openRetentionOfferPopup(reason: BillingCancellationReason): void {
		this.popupService
			.createModal(AccountBillingCancellationFlowPopupRetentionOfferComponent, {
				popupClass: `${this.popupClass} popup-retention-offer`,
			})
			.open({
				reason,
			});
	}

	createReactivatePopup(): PopupModal {
		return this.popupService.createModal(
			AccountBillingCancellationFlowPopupReactivateComponent,
			{
				popupClass: `${this.popupClass} popup-reactivate`,
			},
		);
	}

	// DOWNGRADE TO FREE
	downgradeToFree(): void {
		const targetPlan = this.billingService.availablePlans.find((plan) => plan.isFree)!;
		const reallyDowngrade = () => {
			// this.sendFeedback();

			this.billingService
				.switchPlanWithoutConfirm(targetPlan)
				.then(() => {
					this.navigationService.transitionToRoute('settings.billing.index');
				})
				.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
		};

		if (!this.billingService.checkForDowngradeLimits(targetPlan)) {
			const popup = this.prepareDowngradeLimitationsPopup();
			popup.on('confirm', this, () => {
				// this.triggerDowngradeForFreeEvent();
				reallyDowngrade();
			});
			popup.open({ targetPlan });
		} else {
			// this.triggerDowngradeForFreeEvent();
			this.popupService.openConfirmPopup({
				onConfirm: reallyDowngrade,
				title: 'Are you sure you want to downgrade to Free plan?',
				text: '',
				confirmAction: 'Downgrade',
			});
		}
	}

	// STARTING CANCELLATION FLOW
	startCancellation(): void {
		this.latestActivePlan = this.billingService.activePlan;
		this.openSomethingWrongPopup();
	}

	requestIsAccountReviewOnly(): Promise<boolean> {
		if (!this.canRequestSetupHelp) {
			return Promise.resolve(false);
		}

		return this.requestService
			.api<APIData.Billing.CancellationOptionsListResponse | null>(
				'GET',
				'/account/cancellation/options',
				null,
				{ skipShowingErrorInfolayer: true },
			)
			.then(
				(options) =>
					!!options?.some(
						(option) =>
							option.type === BillingCancellationOptionType.ACCOUNT_REVIEW_ONLY,
					),
			)
			.catch(() => false);
	}

	openCancellationMeeting(
		origin: 'cancellation' | 'cancellation_offer' | 'cancellation_review_only',
	): void {
		window.open(Defaults.CANCELLATION_MEETING_URL, '_blank');
		this.analyticsService.event(TrackingEvent.CALL_INITIATED, { origin });
	}

	reactivateSubscription(): Promise<unknown> {
		return this.requestService
			.api('POST', '/account/cancellation/reactivate')
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	private feedbackSessionStorageKey = 'cancellationFlowFeedback';

	getFeedback(): CancellationFeedback | null {
		let feedback: CancellationFeedback | null = null;
		let feedbackStringifiedJSON: string | null = null;

		try {
			feedbackStringifiedJSON = sessionStorage.getItem(this.feedbackSessionStorageKey);
		} catch (_e) {
			// just ignore, not important enough to warn user
		}

		if (feedbackStringifiedJSON) {
			feedback = JSON.parse(feedbackStringifiedJSON) as CancellationFeedback;
		}

		return feedback;
	}

	saveFeedback(feedback: CancellationFeedback): void {
		try {
			sessionStorage.setItem(this.feedbackSessionStorageKey, JSON.stringify(feedback));
		} catch (_e) {
			// just ignore, not important enough to warn user
		}
	}

	private removeFeedback(): void {
		try {
			sessionStorage.removeItem(this.feedbackSessionStorageKey);
		} catch (_e) {
			// just ignore, not important enough to warn user
		}
	}

	sendFeedback(): Promise<void> | void {
		const feedback = this.getFeedback();
		if (!feedback) {
			return;
		}

		if (!this.billingService.isTrial && !!feedback.choice?.length) {
			this.triggerUnsubscribeFeedbackEvent();
			this.requestService
				.api('POST', '/account/feedback', feedback)
				.then(() => {
					this.removeFeedback(); // cleanup
				})
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));

			return;
		}

		if (this.billingService.isTrial && feedback.trial_downgrade_feedback) {
			this.triggerUnsubscribeFeedbackEvent();
			this.requestService
				.api('POST', '/account/feedback-trial', feedback)
				.then(() => {
					this.removeFeedback(); // cleanup
				})
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		}
	}

	// TRACKING
	trackingOriginForCancellationFlowStarted: TrackingOriginForCancellationFlowStarted | null =
		null;
	trackingOriginForBillingPeriodChangePopup?: TrackingOriginForSubscriptionChangePopup;

	triggerCancellationFlowStartedEvent(): void {
		this.analyticsService.event(TrackingEvent.CANCELATION_FLOW_STARTED, {
			origin: this.trackingOriginForCancellationFlowStarted,
			type: this.type,
		});
	}

	triggerCancelSubscriptionInitialEvent(): void {
		this.analyticsService.event(TrackingEvent.CANCEL_SUBSCRIPTION_INITIAL, { type: this.type });
	}

	private triggerUnsubscribeFeedbackEvent(): void {
		const { choice, trial_downgrade_feedback } = this.getFeedback() || {};

		this.analyticsService.event(TrackingEvent.UNSUBSCRIBE_FEEDBACK, {
			choice,
			type: this.type,
			trial_downgrade_feedback,
			subscription_type: this.billingService.getTrackingSubscriptionType(),
		});

		if (typeof choice !== 'undefined') {
			this.analyticsService.properties({ unsubscribe_category: choice }, ['user', 'company']);
		}

		if (typeof trial_downgrade_feedback !== 'undefined') {
			this.analyticsService.properties({ trial_downgrade_feedback }, ['user', 'company']);
		}
	}

	private type = 'cancel';
}

declare module '@ember/service' {
	interface Registry {
		'flow/cancellation': FlowCancellationService;
	}
}
